import Cookies from "js-cookie";
import { serverBaseUrl } from "../config/envs";

async function request(path = "", options = {}) {
  const token = Cookies.get("token");
  let response;
  try {
    response = await fetch(`${serverBaseUrl}/cp_document_template_drafts${path}`, {
      ...options,
      headers: { ...(options.body instanceof FormData ? {} : { "Content-Type": "application/json" }), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
  } catch {
    throw new Error("Could not reach CP document drafts. Check your connection and try again. Any unsaved edits are kept on this page.");
  }
  let body = null;
  try { body = await response.json(); }
  catch { /* A proxy can reject the request with HTML rather than the API envelope. */ }

  const validData = body?.data !== null && typeof body?.data === "object" && !Array.isArray(body.data);
  if (response.ok && body?.success === true && validData) return body.data;

  const serverMessage = typeof body?.message === "string" && body.message.trim() ? body.message : null;
  let message;
  if (response.status === 404 && body?.messageCode === 1156) {
    message = "CP document drafts are not available right now. Any unsaved edits are kept on this page.";
  } else if (response.status === 401) {
    message = "Your Sign session is missing or expired. Any unsaved edits are kept on this page. Sign in again in another tab before retrying.";
  } else if (response.status === 400 && body?.messageCode === 1151) {
    const errors = body?.data?.validation_errors;
    message = Array.isArray(errors) && errors.length && errors.every((entry) => typeof entry === "string" && entry.trim())
      ? errors.join(" ") : serverMessage || "Review the document before publishing.";
  } else if (response.status === 409) {
    message = body?.messageCode === 1154
      ? "Publishing is not enabled. Your draft is still saved; try again after the rollout is ready."
      : body?.messageCode === 1152
        ? "The saved draft or its document mappings changed. Review the publish impact again before continuing."
        : "This draft changed elsewhere. Your edits are still here. Reload only if you want to discard them.";
  } else if (response.status === 500 && body?.messageCode === 1050) {
    // The current backend uses this code for both invalid tokens and Auth outages.
    message = "Could not verify your session. Any unsaved edits are kept on this page. Please try again; if this continues, sign in again in another tab.";
  } else if (response.status === 413) {
    message = serverMessage || "The upload was rejected as too large. Choose a smaller PDF; if it is already 10 MB or smaller, contact support.";
  } else if (response.status >= 500) {
    message = [1137, 1153].includes(body?.messageCode) && serverMessage ? serverMessage : "CP document drafts are temporarily unavailable. Any unsaved edits are kept on this page. Please try again.";
  } else if (response.ok) {
    message = "The server returned an unexpected response. Any unsaved edits are kept on this page. Please try again.";
  } else {
    message = serverMessage || "Could not access CP document drafts. Any unsaved edits are kept on this page. Please try again.";
  }
  const error = new Error(message);
  error.status = response.status;
  error.code = body?.messageCode;
  throw error;
}

export const listCpDrafts = () => request();
export const getCpDraft = (id) => request(`/${encodeURIComponent(id)}`);
export const createCpDraft = (id, templateVersionId, sourceDraftId) => request("", {
  method: "POST", body: JSON.stringify(sourceDraftId !== undefined ? { id, source_draft_id: sourceDraftId }
    : templateVersionId === undefined ? { id } : { id, template_version_id: templateVersionId }),
});
export const saveCpDraft = (draft) => request(`/${encodeURIComponent(draft.id)}`, {
  method: "PUT",
  body: JSON.stringify({ expected_revision: draft.revision, name: draft.name, document_kind: draft.document_kind, signing_scope: draft.signing_scope, state_ids: draft.state_ids, license_classes: draft.license_classes, signer_roles: draft.signer_roles, pdf: draft.pdf, fields: draft.fields }),
});
export const uploadCpDraftPdf = (id, revision, file) => {
  const body = new FormData();
  body.append("file", file);
  body.append("expected_revision", String(revision));
  return request(`/${encodeURIComponent(id)}/pdf`, { method: "POST", body });
};

export const getCpDraftPublication = (id) => request(`/${encodeURIComponent(id)}/publication`);
export const publishCpDraft = (id, publication, replaceExisting) => request(`/${encodeURIComponent(id)}/publish`, {
  method: "POST", body: JSON.stringify({ expected_revision: publication.revision, expected_mappings: publication.expected_mappings, replace_existing: replaceExisting }),
});
export const retireCpMapping = (mapping) => request(`/mappings/${encodeURIComponent(mapping.id)}/retire`, {
  method: "POST", body: JSON.stringify({ expected_template_version_id: mapping.template_version_id }),
});
